import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext/AuthContext";
import { getSocket } from "../auth/AuthContext/socketManager";
import LogoutIcon from "@mui/icons-material/Logout";
import MenuIcon from "@mui/icons-material/Menu";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  AppBar,
  Toolbar,
  IconButton,
  Menu,
  MenuItem,
  Button,
  Tabs,
  Tab,
  Box,
  Switch,
  Typography,
  Drawer,
  Divider,
  ListItemText,
} from "@mui/material";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import AssignmentIcon from "@mui/icons-material/Assignment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTachographDigital, faGear, faUsers, faCloud, faArrowRightArrowLeft } from "@fortawesome/free-solid-svg-icons";
import logoLight from "../../assets/images/logo_light.png";
import logoDark from "../../assets/images/logo_dark.png";

interface NavbarProps {
  toggleDarkMode: () => void;
  darkMode: boolean;
}

const Navbar: React.FC<NavbarProps> = ({ toggleDarkMode, darkMode }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [anchorElMobile, setAnchorElMobile] = useState<null | HTMLElement>(null);
  const [profileAnchor, setProfileAnchor] = useState<null | HTMLElement>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [socketDisconnected, setSocketDisconnected] = useState(false);
  const socketFailureCount = useRef(0);
  const { isAuthenticated, userRole, logout, userName } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const settingsActive = pathname.startsWith("/users/") || pathname === "/bookings/recovery" || pathname.startsWith("/bookings/recovery/");
  const activeTab = pathname === "/OperationalDashboard"
    ? "/OperationalDashboard"
    : pathname === "/list" || pathname.startsWith("/list/")
      ? "/list"
      : false;

  useEffect(() => {
    socketFailureCount.current = 0;
    setSocketDisconnected(false);
    if (!isAuthenticated) return;
    const socket = getSocket();
    if (!socket) return;

    const handleConnect = () => {
      socketFailureCount.current = 0;
      setSocketDisconnected(false);
    };
    const handleDisconnect = () => {
      socketFailureCount.current = 0;
      setSocketDisconnected(true);
    };
    const handleConnectError = (error: Error) => {
      if (error.message === "SESSION_EXPIRED" || error.message === "UNAUTHORIZED") {
        socketFailureCount.current = 0;
        setSocketDisconnected(true);
        return;
      }
      socketFailureCount.current += 1;
      if (socketFailureCount.current >= 3) setSocketDisconnected(true);
    };

    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);
    socket.on("connect_error", handleConnectError);
    return () => {
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
      socket.off("connect_error", handleConnectError);
    };
  }, [isAuthenticated]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElMobile(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setAnchorElMobile(null);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    handleMenuClose();
  };

  const handleLogout = () => {
    setProfileAnchor(null);
    logout();
    navigate("/login");
  };

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  return (
    <>
      <AppBar
        position="static"
        elevation={0}
        sx={{ borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}
      >
        <Toolbar sx={{ minHeight: { xs: "56px", md: "64px" }, px: { xs: 1, sm: 2 }, minWidth: 0 }}>
          <IconButton
            edge="start"
            color="inherit"
            aria-label="menu"
            onClick={handleDrawerToggle}
            sx={{ display: { xs: "flex", md: "none" }, mr: 1, p: 1.25 }}
          >
            <MenuIcon />
          </IconButton>


          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 }, minWidth: 0, flex: { xs: 1, md: '0 1 auto' } }}>
            <Box
              component="img"
              src={darkMode ? logoDark : logoLight}
              alt="Slow Tour Tuscany"
              onClick={() => navigate("/OperationalDashboard")}
              sx={{
                cursor: "pointer",
                height: { xs: 32, md: 38 },
                width: "auto",
                maxWidth: { xs: 128, sm: 180, md: 240 },
                objectFit: "contain",
                objectPosition: "left center",
                minWidth: 0,
                flexShrink: 0,
              }}
            />
            {socketDisconnected && (
              <Typography variant="caption" color="error.main" role="status"
                sx={{ display: { xs: "none", md: "block" }, ml: 1, maxWidth: 210, lineHeight: 1.2 }}>
                Aggiornamenti in tempo reale non disponibili
              </Typography>
            )}
            {socketDisconnected && (
              <Typography variant="caption" color="error.main" role="status"
                sx={{ display: { xs: "block", md: "none" }, fontSize: "0.65rem", lineHeight: 1.2 }}>
                Aggiornamenti in tempo reale non disponibili
              </Typography>
            )}
          </Box>

          <Box sx={{ display: { xs: "none", md: "flex" }, alignItems: "center", gap: 0.75, ml: "auto" }}>
            <Tabs
              value={activeTab}
              aria-label="Navigazione principale"
              textColor="inherit"
              sx={{
                minHeight: 64,
                "& .MuiTabs-indicator": { backgroundColor: "#80CC00", height: 3 },
                "& .MuiTab-root": {
                  minHeight: 64,
                  width: "auto",
                  minWidth: { md: 144, lg: 164 },
                  px: 2,
                  py: 1,
                  textTransform: "none",
                  fontWeight: 500,
                  fontSize: "0.9rem",
                  lineHeight: 1.3,
                  borderRadius: 1,
                  transition: "background-color 150ms ease",
                  "&:hover": { backgroundColor: "action.hover" },
                  "&.Mui-selected": { color: "#80CC00" },
                },
              }}
            >
              <Tab
                value="/OperationalDashboard"
                label="Dashboard Operativa"
                icon={<FontAwesomeIcon icon={faTachographDigital} />}
                iconPosition="start"
                sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
                onClick={() => handleNavigation("/OperationalDashboard")}
              />
              <Tab
                value="/list"
                label="Prenotazioni"
                icon={<AssignmentIcon sx={{ fontSize: "1.35rem" }} />}
                iconPosition="start"
                wrapped
                onClick={() => handleNavigation("/list")}
              />
            </Tabs>
            {userRole === "1" && (
              <>
                <Button
                  color="inherit"
                  onClick={handleMenuOpen}
                  startIcon={<FontAwesomeIcon icon={faGear} />}
                  endIcon={<KeyboardArrowDownIcon sx={{ fontSize: "1.1rem" }} />}
                  aria-current={settingsActive ? "page" : undefined}
                  aria-haspopup="menu"
                  aria-expanded={Boolean(anchorEl)}
                  sx={{
                    textTransform: "none",
                    fontWeight: 500,
                    fontSize: "0.9rem",
                    px: 2,
                    py: 1,
                    minHeight: 64,
                    position: "relative",
                    color: settingsActive ? "#80CC00" : "inherit",
                    opacity: settingsActive ? 1 : 0.6,
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: 3,
                      backgroundColor: settingsActive ? "#80CC00" : "transparent",
                    },
                  }}
                >
                  Settings
                </Button>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                  slotProps={{
                    paper: {
                      elevation: 0,
                      sx: {
                        border: (theme) => `1px solid ${theme.palette.divider}`,
                        mt: 0.5,
                        borderRadius: (theme) => theme.shape.borderRadius / 1.5,
                      },
                    },
                  }}
                >{/* 
                  <MenuItem onClick={() => handleNavigation("/users/new")} sx={{ fontSize: "0.875rem" }}>
                    Nuovo
                  </MenuItem>
                 */}
                  <MenuItem onClick={() => handleNavigation("/users/list")} sx={{ fontSize: "0.875rem" }}>
                    <FontAwesomeIcon icon={faUsers} fixedWidth style={{ marginRight: 12 }} />Utenti
                  </MenuItem>
                    <MenuItem onClick={() => handleNavigation("/users/online")} sx={{ fontSize: "0.875rem" }}>
                      <FontAwesomeIcon icon={faCloud} fixedWidth style={{ marginRight: 12 }} />Online
                    </MenuItem>
                    <MenuItem onClick={() => handleNavigation('/bookings/recovery')} sx={{ fontSize: '0.875rem' }}><FontAwesomeIcon icon={faArrowRightArrowLeft} fixedWidth style={{ marginRight: 12 }} />Recupero Bokun</MenuItem>
                </Menu>
              </>
            )}

          </Box>

            {isAuthenticated && (
              <>
                <Button
                  onClick={(event) => setProfileAnchor(event.currentTarget)}
                  endIcon={<KeyboardArrowDownIcon sx={{ fontSize: "1.1rem" }} />}
                  aria-expanded={Boolean(profileAnchor)}
                  aria-controls={profileAnchor ? "desktop-profile-menu" : undefined}
                  sx={{
                    color: "#80CC00",
                    textTransform: "none",
                    fontWeight: 500,
                    fontSize: { xs: "0.8rem", sm: "0.9rem", md: "1rem" },
                    px: { xs: 0.75, md: 1.5 },
                    py: 1,
                    ml: { xs: "auto", md: 1 },
                    minHeight: { xs: 56, md: 64 },
                    minWidth: 0,
                    maxWidth: { xs: "50%", md: "none" },
                    flexShrink: 1,
                    "& .MuiButton-endIcon": { flexShrink: 0, ml: 0.5 },
                    borderRadius: 1,
                    transition: "background-color 150ms ease",
                    "&:hover": { backgroundColor: "action.hover" },
                  }}
                >
                  <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {userName || "Profilo"}
                  </Box>
                </Button>
                <Menu
                  id="desktop-profile-menu"
                  anchorEl={profileAnchor}
                  open={Boolean(profileAnchor)}
                  onClose={() => setProfileAnchor(null)}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                  slotProps={{ paper: { elevation: 0, sx: { mt: 0.5, minWidth: 220, borderRadius: 2, border: (theme) => `1px solid ${theme.palette.divider}` } } }}
                >
                  <MenuItem onClick={toggleDarkMode}>
                    {darkMode ? <LightModeIcon sx={{ mr: 1.5 }} /> : <DarkModeIcon sx={{ mr: 1.5 }} />}
                    <ListItemText primary={darkMode ? "Modalità chiara" : "Modalità scura"} />
                  </MenuItem>
                  <Divider />
                  <MenuItem onClick={handleLogout}>
                    <LogoutIcon sx={{ mr: 1.5 }} />
                    <ListItemText primary="Logout" />
                  </MenuItem>
                </Menu>
              </>
            )}
        </Toolbar>
      </AppBar>

      {/* DRAWER MOBILE */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={handleDrawerToggle}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            boxShadow: "none",
            borderRight: (theme) => `1px solid ${theme.palette.divider}`,
            width: { xs: 'min(86vw, 300px)', sm: 300 }
          }
        }}
      >
        <Box sx={{ width: "auto", p: 1.5 }}>
          <Typography variant="subtitle2" sx={{ px: 2, py: 1, color: "text.secondary", fontWeight: 600 }}>
            Menu
          </Typography>

          <MenuItem onClick={() => { handleNavigation("/OperationalDashboard"); handleDrawerToggle(); }} sx={{ borderRadius: 1, mb: 0.5 }}>
            <FontAwesomeIcon icon={faTachographDigital} fixedWidth style={{ marginRight: 12, fontSize: "1.25rem" }} />
            <ListItemText primary="Dashboard Operativa" primaryTypographyProps={{ fontSize: "0.9rem" }} />
          </MenuItem>

          <MenuItem onClick={() => { handleNavigation("/list"); handleDrawerToggle(); }} sx={{ borderRadius: 1, mb: 0.5 }}>
            <AssignmentIcon sx={{ marginRight: 1.5, fontSize: "1.25rem" }} />
            <ListItemText primary="Prenotazioni" primaryTypographyProps={{ fontSize: "0.9rem" }} />
          </MenuItem>

          {userRole === "1" && (
            <>
              <MenuItem onClick={handleMobileMenuOpen} sx={{ borderRadius: 1, mb: 0.5 }}>
                <FontAwesomeIcon icon={faGear} fixedWidth style={{ marginRight: 12, fontSize: "1.25rem" }} />
                <ListItemText primary="Settings" primaryTypographyProps={{ fontSize: "0.9rem" }} />
                {Boolean(anchorElMobile) ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </MenuItem>
              <Menu
                anchorEl={anchorElMobile}
                open={Boolean(anchorElMobile)}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                slotProps={{
                  paper: {
                    elevation: 0,
                    sx: {
                      border: (theme) => `1px solid ${theme.palette.divider}`,
                      borderRadius: (theme) => theme.shape.borderRadius / 1.5,
                      minWidth: 180,
                    },
                  },
                }}
              >
                {/* <MenuItem onClick={() => { handleNavigation("/users/new"); handleDrawerToggle(); }} sx={{ fontSize: "0.875rem" }}>Nuovo</MenuItem>*/}
                <MenuItem onClick={() => { handleNavigation("/users/list"); handleDrawerToggle(); }} sx={{ fontSize: "0.875rem" }}><FontAwesomeIcon icon={faUsers} fixedWidth style={{ marginRight: 12 }} />Utenti</MenuItem>
                  <MenuItem onClick={() => { handleNavigation("/users/online"); handleDrawerToggle(); }} sx={{ fontSize: "0.875rem" }}><FontAwesomeIcon icon={faCloud} fixedWidth style={{ marginRight: 12 }} />Online</MenuItem>
                  <MenuItem onClick={() => { handleNavigation('/bookings/recovery'); handleDrawerToggle(); }} sx={{ fontSize: '0.875rem' }}><FontAwesomeIcon icon={faArrowRightArrowLeft} fixedWidth style={{ marginRight: 12 }} />Recupero Bokun</MenuItem>
              </Menu>
            </>
          )}

          <Divider sx={{ my: 1.5 }} />

          <Box sx={{ pl: 2, pr: 1, display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {darkMode ? <LightModeIcon sx={{ fontSize: "1.1rem", mr: 1.5 }} /> : <DarkModeIcon sx={{ fontSize: "1.1rem", mr: 1.5 }} />}
              <Typography variant="body2" sx={{ fontSize: "0.9rem" }}>Tema Scuro</Typography>
            </Box>
            <Switch checked={darkMode} onChange={toggleDarkMode} size="small" />
          </Box>

          {isAuthenticated && (
            <MenuItem onClick={() => { handleLogout(); handleDrawerToggle(); }} sx={{ borderRadius: 1, mt: 1, color: "error.main" }}>
              <LogoutIcon sx={{ marginRight: 1.5, fontSize: "1.25rem" }} />
              <ListItemText primary="Logout" primaryTypographyProps={{ fontSize: "0.9rem" }} />
            </MenuItem>
          )}
        </Box>
      </Drawer>
    </>
  );
};

export default Navbar;
